import create from "zustand"
import { useI18next } from "gatsby-plugin-react-i18next"
import { useCallback, useMemo } from "react"
import getSelectedMenuPath from "./side-menu-structure/getSelectedMenuPath"
import useMenuStructure from "./side-menu-structure/useMenuStructure"

export type SortDirection = 'asc' | 'desc'

type SideMenuStore = {
  isSideMenuOpen: boolean
  isAlphabetical: boolean
  sortDirection: SortDirection
  openMenuPath: string[] | null
  allLevelsOpen: boolean
  searchQuery: string
}

const useSideMenuStore = create<SideMenuStore>(() => ({
  isSideMenuOpen: false,
  isAlphabetical: false,
  sortDirection: 'asc',
  currentLocationPathname: null,
  openMenuPath: null,
  allLevelsOpen: false,
  searchQuery: '',
}))

const useSelectedMenuPath = (): string[] => {
  const { originalPath } = useI18next()
  const menuStructure = useMenuStructure()
  return useMemo(
    () => getSelectedMenuPath(originalPath, menuStructure), 
    [originalPath, menuStructure],
  )
}

export const useSelectedCategoryId = (): string | undefined => 
  useSelectedMenuPath()[0]

export const useSelectedSubcategoryId = (): string | undefined => 
  useSelectedMenuPath()[1]

export const useSelectedElementId = (): string | undefined => 
  useSelectedMenuPath()[2]

export const useOpenCategoryId = (): string | undefined => {
  const openMenuPath = useSideMenuStore((state) => state.openMenuPath)
  const selectedMenuPath = useSelectedMenuPath()
  if (openMenuPath) {
    return openMenuPath[0]
  }
  if (selectedMenuPath[1]) {
    return selectedMenuPath[0]
  }
}

export const openCategory = (categoryId: string) => {
  useSideMenuStore.setState({ openMenuPath: [categoryId] })
}

export const closeAllMenuItems = () => {
  useSideMenuStore.setState({ openMenuPath: [] })
}

export const clearOpenSelection = () => {
  useSideMenuStore.setState({ openMenuPath: null })
}

export const useOpenSubcategoryId = (): string | undefined => {
  const openMenuPath = useSideMenuStore((state) => state.openMenuPath)
  const selectedMenuPath = useSelectedMenuPath()
  if (openMenuPath) {
    return openMenuPath[1]
  }
  if (selectedMenuPath[2]) {
    return selectedMenuPath[1]
  }
}

export const useOpenSubcategory = () => {
  const openCategoryId = useOpenCategoryId()
  return useCallback((subcategoryId: string) => {
    useSideMenuStore.setState({ 
      openMenuPath: openCategoryId ? [openCategoryId, subcategoryId] : [],
    })
  }, [openCategoryId])
}

export const useCloseSubcategory = () => {
  const openCategoryId = useOpenCategoryId()
  return () => {
    useSideMenuStore.setState({ 
      openMenuPath: openCategoryId ? [openCategoryId] : [],
    })
  }
}

export const useIsMenuOpen = (): boolean => 
  useSideMenuStore((state) => state.isSideMenuOpen)

export const setIsMenuOpen = (isSideMenuOpen: boolean) => {
  useSideMenuStore.setState({ isSideMenuOpen })
}

export const toggleSideMenu = () => {
  useSideMenuStore.setState((state) => ({ isSideMenuOpen: !state.isSideMenuOpen }))
}

export const useIsAlphabetical = (): boolean =>
  useSideMenuStore((state) => state.isAlphabetical)

export const getCurrentIsAlphabetical = () =>
  useSideMenuStore.getState().isAlphabetical

export const setIsAlphabetical = (isAlphabetical: boolean) => {
  useSideMenuStore.setState({ isAlphabetical })
}

export const useSortDirection = (): SortDirection => 
  useSideMenuStore((state) => state.sortDirection)

export const setSortDirection = (sortDirection: SortDirection) =>{
  useSideMenuStore.setState({ sortDirection })
}

export const useSearchQuery = (): string =>
  useSideMenuStore((state) => state.searchQuery)

export const setSearchQuery = (searchQuery: string) => {
  useSideMenuStore.setState({ searchQuery })
}
